import { Link } from 'react-router-dom';
import { Trophy, FileText, Users, CheckSquare, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
            CompCreator
          </h1>
          <div className="flex items-center gap-4">
            <Link to="/coming-competitions">
              <Button variant="ghost" data-testid="nav-coming-competitions">Coming Competitions</Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" data-testid="nav-login" className="rounded-full">Login</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="nav-register" className="btn-primary">Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>
      
      <section className="hero-gradient py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7 fade-in">
              <h2 className="text-5xl md:text-6xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
                Streamline Your Artistic Rollerskating Competitions
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
                From paperwork to promotion, CompCreator simplifies every aspect of managing rollerskating competitions. Built by volunteers, for volunteers.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" data-testid="hero-get-started-button" className="btn-primary">Get Started <ArrowRight className="ml-2 h-5 w-5" /></Button>
                </Link>
                <Link to="/coming-competitions">
                  <Button size="lg" variant="outline" data-testid="hero-view-competitions-button" className="btn-outline">View Competitions</Button>
                </Link>
              </div>
            </div>
            <div className="md:col-span-5 fade-in">
              <img
                src="https://customer-assets.emergentagent.com/job_fa51404a-2914-4160-a4cc-85bb75b5164a/artifacts/dlyz4vt5_Skating%20Floor.jpg"
                alt="Skating Floor"
                className="rounded-2xl shadow-2xl w-full object-cover"
                style={{ height: '400px' }}
              />
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Everything You Need to Run a Competition
            </h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Powerful features designed specifically for artistic rollerskating competition organisers
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-8 card-hover bg-white rounded-2xl shadow-sm border border-stone-100">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#2D4A3E' }}>
                <Trophy className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-xl font-medium mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Competition Management</h4>
              <p className="text-muted-foreground">Create, organise, and manage competitions with ease. Track all details in one central location.</p>
            </Card>
            
            <Card className="p-8 card-hover bg-white rounded-2xl shadow-sm border border-stone-100">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#D4A373' }}>
                <FileText className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-xl font-medium mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Document Generation</h4>
              <p className="text-muted-foreground">Generate professional PDFs, Word docs, and Excel sheets with a single click.</p>
            </Card>
            
            <Card className="p-8 card-hover bg-white rounded-2xl shadow-sm border border-stone-100">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#E9C46A' }}>
                <Users className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-xl font-medium mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Team Collaboration</h4>
              <p className="text-muted-foreground">Work together with role-based permissions for super users and standard users.</p>
            </Card>
            
            <Card className="p-8 card-hover bg-white rounded-2xl shadow-sm border border-stone-100">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#2A9D8F' }}>
                <CheckSquare className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-xl font-medium mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Approval Workflow</h4>
              <p className="text-muted-foreground">Streamlined review and approval process for competition pages before publication.</p>
            </Card>
          </div>
        </div>
      </section>
      
      <section className="py-24" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1754300681803-61eadeb79d10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxnb2xkJTIwdHJvcGh5JTIwY3VwJTIwY29tcGV0aXRpb24lMjBhd2FyZHxlbnwwfHx8fDE3NzM2MzUzMDJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Competition Award"
                className="rounded-2xl shadow-xl w-full object-cover"
                style={{ height: '400px' }}
              />
            </div>
            <div>
              <h3 className="text-3xl md:text-4xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Built for the Skating Community
              </h3>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                CompCreator was created by volunteers who understand the unique challenges of organising artistic rollerskating competitions.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#2D4A3E' }}>
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Content Library</p>
                    <p className="text-muted-foreground">Manage schedules, disciplines, grades, dances, and figures in one place</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#2D4A3E' }}>
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Venue & Club Management</p>
                    <p className="text-muted-foreground">Keep track of all venues and participating organisations</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#2D4A3E' }}>
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Public Competition Pages</p>
                    <p className="text-muted-foreground">Share competition details with the community once approved</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl md:text-4xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Simplify Your Competition Management?
          </h3>
          <p className="text-lg text-muted-foreground mb-8">
            Join competition organisers who are already saving time with CompCreator
          </p>
          <Link to="/register">
            <Button size="lg" data-testid="cta-get-started-button" className="btn-primary">Get Started <ArrowRight className="ml-2 h-5 w-5" /></Button>
          </Link>
        </div>
      </section>
      
      <footer className="py-12 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 text-center text-muted-foreground">
          <p>© 2026 CompCreator. Built with care for the artistic rollerskating community.</p>
        </div>
      </footer>
    </div>
  );
}
